import React, { useState, useRef, forwardRef, useEffect, useId } from "react";
import useFormattedDate from "../../../context/useFormattingUtils";
import "react-datepicker/dist/react-datepicker.css";
import { capitalizeFirstLetter } from "../../../utils/helper/common";

/* ---------- Utility: Generate Safe ID ---------- */

const generateSafeId = (id, name, alt, label, placeholder, reactId) => {
  if (id) return id;

  const source = name || alt || label || placeholder || reactId;

  const sanitized = source
    ?.toString()
    .trim()
    .toLowerCase()
    .replace(/:/g, "")          // remove React colon ids
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "");

  return sanitized || `field-${reactId.replace(/:/g, "")}`;
};

/* ---------- Reusable Input ---------- */

const ReusableInput = ({
  id,
  type = "text",
  value,
  onChange,
  label,
  placeholder = "",
  style = {},
  required = false,
  width,
  disabled = false,
  onBlur,
  onFocus,
  onKeyDown,
  onInput,
  name,
  alt,
  error = "",
  readOnly,
  autoCapitalize = false
}) => {

  const reactId = useId();
  const generatedId = generateSafeId(id, name, alt, label, placeholder, reactId);

  const handleChange = (e) => {
    if (autoCapitalize && type === "text") {
      const capitalizedValue = capitalizeFirstLetter(e.target.value);

      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: capitalizedValue
        }
      };

      onChange(syntheticEvent);
    } else {
      onChange(e);
    }
  };

  return (
    <div className="form-group product-field">
      <input
        id={generatedId}
        data-testid={generatedId}
        disabled={disabled}
        type={type}
        className="form-control-sales"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onInput={onInput}
        style={{ ...style }}
        required={required}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        name={name}
        readOnly={readOnly}
        maxLength={type === "tel" ? 20 : type === "email" ? 40 : 50}
      />

      <label htmlFor={generatedId} className="floating-label">
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </label>

      {error && (
        <div
          className="error-msg"
          style={{
            position: "absolute",
            left: "0",
            color: "#CF0A0A",
            fontSize: "12px",
            marginTop: "5px",
            // marginLeft: "50px",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

/* ---------- Select Input ---------- */

const SelectInput = ({
  id,
  value,
  onChange,
  options = [],
  label,
  placeholder = "",
  style = {},
  required = false,
  width,
  disabled = false,
  onBlur,
  onFocus,
  name,
  alt,
  error = "",
}) => {

  const reactId = useId();
  const generatedId = generateSafeId(id, name, alt, label, placeholder, reactId);

  return (
    <div className="form-group product-field">
      <select
        id={generatedId}
        data-testid={generatedId}
        disabled={disabled}
        className="form-control-setting select"
        value={value || ""}
        onChange={onChange}
        style={{ ...style }}
        required={required}
        onBlur={onBlur}
        onFocus={onFocus}
        name={name}
      >
        <option value="" disabled>
          {placeholder || "Select an option"}
        </option>

        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>

      <label htmlFor={generatedId} className="floating-label-option">
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </label>

      {error && (
        <div
          className="error-msg"
          style={{
            position: "absolute",
            left: "0",
            color: "#CF0A0A",
            fontSize: "12px",
            marginTop: "5px",
            // marginLeft: "50px",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

/* ---------- Date Input ---------- */

const ReusableDateInput = forwardRef(
  (
    {
      id,
      value,
      onChange,
      label,
      required,
      inputStyle = {},
      containerStyle = {},
      wrapperStyle = {},
      placeholder = "Select date",
      name,
      alt,
      error = "",
    },
    ref
  ) => {

    const { formatDate } = useFormattedDate();
    const dateInputRef = useRef(null);
    const [displayValue, setDisplayValue] = useState("");

    const reactId = useId();
    const generatedId = generateSafeId(id, name, alt, label, placeholder, reactId);

    useEffect(() => {
      setDisplayValue(formatDisplayValue(value));
    }, [value]);

    const formatDisplayValue = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return formatDate(date);
    };

    const handleDateChange = (e) => {
      const dateString = e.target.value;

      setDisplayValue(formatDisplayValue(dateString));

      if (onChange) {
        onChange({
          target: {
            name: name || e.target.name,
            value: dateString,
          },
        });
      }
    };

    const handleDisplayClick = () => {
      if (dateInputRef.current) {
        dateInputRef.current.focus();

        if (dateInputRef.current.showPicker) {
          try {
            dateInputRef.current.showPicker();
          } catch {
            console.warn("showPicker not supported");
          }
        }
      }
    };

    return (
      <div
        style={{
          position: "relative",
          width: containerStyle?.width || "270px",
          marginBottom: "25px",
          ...containerStyle,
        }}
      >
        <input
          id={generatedId}
          data-testid={generatedId}
          type="text"
          value={displayValue || formatDisplayValue(value)}
          onClick={handleDisplayClick}
          placeholder=" "
          readOnly
          name={name}
          required={required}
          style={{
            width: "100%",
            height: "52px",
            fontSize: "16px",
            padding: "0 4px",
            border: "none",
            borderBottom: "1px solid #9e9e9e",
            borderRadius: "0",
            backgroundColor: "#ffffff",
            outline: "none",
            ...inputStyle
          }}
        />

        <label
          htmlFor={generatedId}
          style={{
            position: "absolute",
            top: displayValue || value ? "-8px" : "16px",
            left: "0px",
            fontSize: displayValue || value ? "14px" : "16px",
            color: displayValue || value ? "#000000" : "#999999",
            backgroundColor: "#fff",
            padding: "0 4px",
            transition: "0.2s ease all",
            pointerEvents: "none",
          }}
        >
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </label>

        <input
          ref={(node) => {
            dateInputRef.current = node;
            if (ref) ref.current = node;
          }}
          type="date"
          value={value || ""}
          onChange={handleDateChange}
          style={{
            position: "absolute",
            opacity: 0,
            pointerEvents: "none",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
          required={required}
        />

        {error && (
          <div
            style={{
              color: "#CF0A0A",
              fontSize: "12px",
              marginTop: "5px",
              position: "absolute",
              left: 0,
              bottom: "-20px",
            }}
          >
            {error}
          </div>
        )}
      </div>
    );
  }
);

export { ReusableInput, SelectInput, ReusableDateInput };